<script setup>
import { ref, onMounted, computed, nextTick, watch } from 'vue'
import Sortable from 'sortablejs'
import SidebarDropdown from '@/components/ToolRail/ToolRailDropdown.vue'
import { getDescriptorById, getAvailableDescriptors } from '@/config/ipTableFields'

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  isFieldEnabled: {
    type: Function,
    required: true
  },
  getFieldOrder: {
    type: Function,
    required: true
  },
  toggleField: {
    type: Function,
    required: true
  },
  setFieldOrder: {
    type: Function,
    required: true
  },
  resetSettings: {
    type: Function,
    required: true
  },
  hasCustomSettings: {
    type: Function,
    required: true
  },
  getEnabledCount: {
    type: Function,
    required: true
  },
  getTotalCount: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['export'])

// Sidebar expansion state
const isExpanded = ref(false)
const selectedFormat = ref('jsonl')
const columnListRef = ref(null)
const columnsDropdownRef = ref(null)
let sortableInstance = null

// Ids of descriptors that are actually renderable for the current data
// (common columns + per present slug). Ungranted/absent enrichments never
// appear in the toggle list.
const availableIds = computed(() => {
  return new Set(getAvailableDescriptors(props.data).map(d => d.id))
})

// Ordered fields for display: the persisted order, filtered to what's available.
const orderedFields = computed(() => {
  return props.getFieldOrder().filter(id => availableIds.value.has(id))
})

function labelFor(id) {
  const d = getDescriptorById(id)
  return d ? d.label : id
}

// Check if there are custom settings
const showReset = computed(() => {
  return props.hasCustomSettings()
})

// Initialize sortable when dropdown opens
function initSortable() {
  nextTick(() => {
    if (columnListRef.value && !sortableInstance) {
      sortableInstance = Sortable.create(columnListRef.value, {
        animation: 150,
        handle: '.drag-handle',
        ghostClass: 'column-ghost',
        dragClass: 'column-drag',
        onEnd: (evt) => {
          const newOrder = [...orderedFields.value]
          const [moved] = newOrder.splice(evt.oldIndex, 1)
          newOrder.splice(evt.newIndex, 0, moved)
          props.setFieldOrder(newOrder)
        }
      })
    }
  })
}

function destroySortable() {
  if (sortableInstance) {
    sortableInstance.destroy()
    sortableInstance = null
  }
}

onMounted(() => {
  // Load saved state from localStorage
  const saved = localStorage.getItem('app-ip-table-sidebar-expanded')
  if (saved !== null) {
    isExpanded.value = saved === 'true'
  }
})

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
  localStorage.setItem('app-ip-table-sidebar-expanded', isExpanded.value.toString())
}

function handleExport() {
  emit('export', selectedFormat.value)
}

function handleToggleField(field) {
  // Don't allow disabling last field
  if (props.isFieldEnabled(field) && props.getEnabledCount() <= 1) {
    return
  }
  props.toggleField(field)
}

function handleReset() {
  props.resetSettings()
  // Close the dropdown after reset
  if (columnsDropdownRef.value) {
    columnsDropdownRef.value.close()
  }
}

function handleColumnsDropdownOpen() {
  initSortable()
}

function handleColumnsDropdownClose() {
  destroySortable()
}
</script>

<template>
  <div class="ip-table-sidebar-wrapper">
    <nav id="tool-sidebar" :class="{ 'expanded': isExpanded }">

      <!-- Export Dropdown -->
      <ul class="navbar-nav mt-auto mb-auto" ondragstart="return false;">
        <SidebarDropdown
          :icon="['fas', 'download']"
          label="export"
          header-label="Export"
          :header-subtext="`${data.length} results`"
          :disabled="data.length === 0"
          :sidebar-expanded="isExpanded"
        >
          <div class="format-toggle">
            <button
              class="format-btn"
              :class="{ active: selectedFormat === 'jsonl' }"
              @click="selectedFormat = 'jsonl'"
            >JSONL</button>
            <button
              class="format-btn"
              :class="{ active: selectedFormat === 'json' }"
              @click="selectedFormat = 'json'"
            >JSON</button>
            <button
              class="format-btn"
              :class="{ active: selectedFormat === 'csv' }"
              @click="selectedFormat = 'csv'"
            >CSV</button>
          </div>

          <div class="dropdown-divider"></div>

          <div class="dropdown-item" @click="handleExport">
            <font-awesome-icon :icon="['fas', 'file-export']" class="item-icon" />
            <span>Export {{ data.length }} results</span>
          </div>
        </SidebarDropdown>

        <!-- Columns Dropdown -->
        <SidebarDropdown
          ref="columnsDropdownRef"
          :icon="['fas', 'table-columns']"
          label="columns"
          header-label="Columns"
          :header-subtext="`${getEnabledCount()} of ${getTotalCount()} visible`"
          :sidebar-expanded="isExpanded"
          @open="handleColumnsDropdownOpen"
          @close="handleColumnsDropdownClose"
        >
          <div ref="columnListRef" class="column-list">
            <div
              v-for="field in orderedFields"
              :key="field"
              class="column-item"
              :class="{ disabled: !isFieldEnabled(field) }"
            >
              <span class="drag-handle">⋮⋮</span>
              <label class="column-checkbox">
                <input
                  type="checkbox"
                  :checked="isFieldEnabled(field)"
                  :disabled="isFieldEnabled(field) && getEnabledCount() <= 1"
                  @change="handleToggleField(field)"
                />
                <span class="checkmark"></span>
              </label>
              <span class="column-label">{{ labelFor(field) }}</span>
            </div>
          </div>

          <div v-if="showReset" class="dropdown-divider"></div>

          <div v-if="showReset" class="dropdown-item reset-item" @click="handleReset">
            <font-awesome-icon :icon="['fas', 'rotate-left']" class="item-icon" />
            <span>Reset to defaults</span>
          </div>
        </SidebarDropdown>
      </ul>

      <!-- Bottom Section - Toggle -->
      <ul class="navbar-nav">
        <li class="nav-item-wrapper">
          <div @click="toggleExpanded()" class="sidebar-nav toggle-nav">
            <font-awesome-icon class="icon" :icon="['fas', isExpanded ? 'angle-right' : 'angle-left']" />
            <span class="nav-text">{{ isExpanded ? 'collapse' : 'expand' }}</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>expand</span>
            </div>
          </div>
        </li>
      </ul>
    </nav>
  </div>
</template>

<style scoped>
.ip-table-sidebar-wrapper {
  position: relative;
  padding: 0;
  padding-left: 0.5rem;
  z-index: 10;
  transition: width 0.3s ease;
}

#tool-sidebar {
  padding: 0;
  background: var(--surface-card);
  display: flex;
  flex-direction: column;
  position: relative;
  width: 85px;
  height: 100%;
  border-radius: var(--radius-xl);
  transition: width 0.3s ease;
}

#tool-sidebar.expanded {
  width: 260px;
}

.nav-item-wrapper {
  position: relative;
  padding: 0.25rem 0.75rem;
}

#tool-sidebar:not(.expanded) .nav-item-wrapper {
  padding: 0.25rem 0.75rem;
}

#tool-sidebar .sidebar-nav {
  padding: 0.75rem 1rem;
  height: auto;
  min-height: 3rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  transition: var(--transition);
  border-radius: var(--radius-lg);
  position: relative;
  gap: 0;
  cursor: pointer;
}

#tool-sidebar .sidebar-nav:hover {
  color: white;
  background-color: var(--surface-hover);
}

#tool-sidebar:not(.expanded) .sidebar-nav {
  justify-content: center;
  padding: 0.75rem 0;
  width: 100%;
}

#tool-sidebar .sidebar-nav .icon {
  transition: margin-left 0.3s ease;
  font-size: 1.1rem;
  width: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Text visibility in collapsed state */
#tool-sidebar:not(.expanded) .nav-text {
  display: none;
}

/* Text visibility in expanded state */
#tool-sidebar.expanded .nav-text {
  display: inline;
  white-space: nowrap;
  font-size: 0.95rem;
}

#tool-sidebar.expanded .sidebar-nav .icon {
  margin-right: 0.75rem;
  transition: all 0.2s ease;
}

/* Popout styles for collapsed state */
.nav-popout {
  display: flex;
  position: absolute;
  right: calc(100% - 1.3rem);
  left: auto;
  top: 0;
  height: 100%;
  background: var(--surface-hover);
  padding: 0.75rem 1rem;
  border-radius: var(--radius-lg);
  align-items: center;
  white-space: nowrap;
  z-index: 100;
  color: white;
  font-size: 0.95rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  text-decoration: none;
}

#tool-sidebar:not(.expanded) .nav-item-wrapper:hover .nav-popout {
  opacity: 1;
  pointer-events: auto;
}

#tool-sidebar:not(.expanded) .nav-item-wrapper:hover .sidebar-nav {
  background-color: var(--surface-hover);
  color: white;
}

/* Format toggle buttons */
.format-toggle {
  display: flex;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
}

.format-btn {
  flex: 1;
  padding: 0.4rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 600;
  background: var(--border);
  border: 1px solid var(--surface-control);
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: var(--transition-fast);
}

.format-btn:hover {
  background: var(--surface-control);
  color: var(--text-primary);
}

.format-btn.active {
  background: rgba(115, 87, 210, 0.2);
  border-color: var(--accent);
  color: var(--accent-text);
}

/* Column list */
.column-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 0.25rem 0;
}

.column-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  transition: background 0.15s ease;
}

.column-item:hover {
  background: rgba(255, 255, 255, 0.03);
}

.column-item.disabled {
  opacity: 0.5;
}

.drag-handle {
  cursor: grab;
  color: var(--text-disabled);
  font-size: 0.75rem;
  padding: 0 0.25rem;
  user-select: none;
}

.drag-handle:hover {
  color: var(--text-muted);
}

.column-checkbox {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.column-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  width: 16px;
  height: 16px;
  background: var(--border);
  border: 1px solid var(--surface-control);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
}

.column-checkbox input:checked ~ .checkmark {
  background: var(--accent);
  border-color: var(--accent);
}

.column-checkbox input:checked ~ .checkmark::after {
  content: '';
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  margin-bottom: 2px;
}

.column-checkbox input:disabled ~ .checkmark {
  opacity: 0.5;
  cursor: not-allowed;
}

.column-label {
  font-size: 0.85rem;
  color: var(--text-primary);
}

/* Drag states */
.column-ghost {
  opacity: 0.4;
  background: var(--accent-soft-2);
}

.column-drag {
  background: var(--surface-page) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Reset item */
.reset-item {
  color: var(--text-muted);
}

.reset-item:hover {
  color: var(--text-primary);
}

/* Scrollbar for column list */
.column-list::-webkit-scrollbar {
  width: 6px;
}

.column-list::-webkit-scrollbar-track {
  background: transparent;
}

.column-list::-webkit-scrollbar-thumb {
  background: var(--surface-control);
  border-radius: var(--radius-xs);
}

.column-list::-webkit-scrollbar-thumb:hover {
  background: var(--surface-control);
}
</style>
